/**
 * ProvenanceStrip — Evidence Assist EA4.
 *
 * Renders a small chip strip at the top of each report section that
 * tells the reader which credibility tier informed the section:
 *
 *   Tier 1 = green chip   — site-specific uploaded document
 *   Tier 2 = blue chip    — cross-report reuse
 *   Tier 3 = amber chip   — public-data regional baseline
 *   Tier 4 = orange chip  — LLM synthesis with caveat
 *
 * The map is computed at gen time and persisted as
 * `report.sectionProvenance[sectionId]`. Pre-EA4 reports (no map) render
 * nothing — the strip is purely additive, never load-bearing.
 */
import React from 'react';
import { FileCheck, Recycle, Sparkles, AlertCircle } from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import { roleColor, roleBorder, type ReportColorRole } from '../../../lib/reportRoleColors';

const MONO = 'var(--report-body-font, inherit)';

interface Props {
  /** Section id — typically matches a sectionId from reportSections.ts. */
  sectionId: string;
  /** The report; we read sectionProvenance[sectionId] off it. */
  report: SiteRagReport;
  /**
   * Active report theme. Optional for back-compat with the section-shell
   * call sites that don't thread a theme; `roleColor`/`roleBorder` are
   * null-safe and resolve DEFAULT_THEME tones when omitted.
   */
  theme?: ReportTheme;
}

/**
 * Tier → editorial ROLE. Tier 1 (site-specific doc) reads as positive,
 * Tier 2 (cross-report reuse) as accent, Tier 3 (regional baseline) as
 * warning, Tier 4 (synthesized) as negative — so the strip follows the
 * active preset's status palette instead of literal success/primary/
 * warning/danger Tailwind tones.
 */
const TIER_PRESENTATION: Record<1 | 2 | 3 | 4, {
  icon: React.ReactNode;
  label: string;
  role: ReportColorRole;
  subtitleLabel: string;
}> = {
  1: {
    icon: <FileCheck className="w-3 h-3" aria-hidden="true" />,
    label: 'Tier 1',
    subtitleLabel: 'Site-specific document',
    role: 'positive',
  },
  2: {
    icon: <Recycle className="w-3 h-3" aria-hidden="true" />,
    label: 'Tier 2',
    subtitleLabel: 'Cross-report reuse',
    role: 'accent',
  },
  3: {
    icon: <Sparkles className="w-3 h-3" aria-hidden="true" />,
    label: 'Tier 3',
    subtitleLabel: 'Regional baseline',
    role: 'warning',
  },
  4: {
    icon: <AlertCircle className="w-3 h-3" aria-hidden="true" />,
    label: 'Tier 4',
    subtitleLabel: 'Synthesized',
    role: 'negative',
  },
};

export const ProvenanceStrip: React.FC<Props> = ({ sectionId, report, theme }) => {
  const entry = report.sectionProvenance?.[sectionId];
  if (!entry) return null;
  const pres = TIER_PRESENTATION[entry.tier];
  const sourcesLine = entry.sources.length > 0
    ? entry.sources.slice(0, 3).join(' · ')
    : pres.subtitleLabel;
  const ink = roleColor(theme, pres.role);
  return (
    <div
      className="flex items-center gap-2 flex-wrap text-[9px] mb-2"
      data-testid={`provenance-strip-${sectionId}`}
      data-tier={entry.tier}
    >
      {/* Restrained small-caps tag — status ink + low-alpha status
          border, not a filled tone pill. */}
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 uppercase tracking-widest font-bold"
        style={{
          fontFamily: MONO,
          letterSpacing: '0.1em',
          color: ink,
          border: `1px solid ${roleBorder(theme, pres.role)}`,
        }}
        aria-label={`${pres.label} ${pres.subtitleLabel}`}
      >
        {pres.icon} {pres.label} · {pres.subtitleLabel}
      </span>
      <span style={{ color: roleColor(theme, 'muted') }}>
        {sourcesLine}
      </span>
      {entry.caveat && (
        <span
          className="italic"
          style={{ color: roleColor(theme, 'muted') }}
          data-testid={`provenance-caveat-${sectionId}`}
        >
          {entry.caveat}
        </span>
      )}
    </div>
  );
};
